import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { preFixImgUrl } from '@/utils'
import { Link, useSearchParams } from 'react-router-dom'
import { LeftOutlined, RightOutlined, StarFilled } from '@ant-design/icons'
import {
  COUPON_STATIC_TEXT,
  TEMP_COUPON_PAGE_SIZE,
  TEMP_COUPON_STORES,
  type TempCouponStore,
  formatCouponParticipants,
  getCouponLocaleText,
} from './useModel'
import HeroBanner from './components/HeroBanner'
import QuickLinks from './components/QuickLinks'

const COUPON_TABS = [
  { id: 'shopping', labelKey: 'home.coupon.tabs.shopping' },
  { id: 'food', labelKey: 'home.coupon.tabs.food' },
  { id: 'transport', labelKey: 'home.coupon.tabs.transport' },
  { id: 'hotel', labelKey: 'home.coupon.tabs.hotel' },
] as const

const readPageIndex = (pageParam: string | null, totalPages: number) => {
  const pageNumber = Number.parseInt(pageParam || '1', 10)

  if (Number.isNaN(pageNumber) || pageNumber < 1 || totalPages <= 0) return 0
  return Math.min(pageNumber - 1, totalPages - 1)
}

type DiscountHighlightParts = {
  value: string
  unit: string
  suffix: string
}

const parseDiscountHighlight = (text: string): DiscountHighlightParts | null => {
  const zhMatch = text.match(/^(\d+)(折)(.*)$/)
  if (zhMatch) {
    return { value: zhMatch[1], unit: zhMatch[2], suffix: zhMatch[3] }
  }
  const offMatch = text.match(/^(\d+%)(\s*)(Off)(.*)$/i)
  if (offMatch) {
    return { value: offMatch[1] + offMatch[2], unit: offMatch[3], suffix: offMatch[4] }
  }
  return null
}

const CouponCardTitle = ({
  merchantName,
  discountText,
}: {
  merchantName: string
  discountText: string
}) => {
  const highlight = discountText ? parseDiscountHighlight(discountText) : null

  return (
    <>
      {merchantName}
      {highlight ? (
        <>
          {' '}
          <span className="inline-flex items-baseline text-[#ff1c1c]">
            <span>{highlight.value}</span>
            <span className="text-[22px]">{highlight.unit}</span>
          </span>
          {highlight.suffix}
        </>
      ) : discountText ? (
        ` ${discountText}`
      ) : null}
    </>
  )
}

export const CouponCard = ({ store }: { store: TempCouponStore }) => {
  const { i18n } = useTranslation()
  const language = i18n.language
  const discountText = store.discount
    ? getCouponLocaleText(store.discount, language)
    : ''
  const title = `${store.merchantName} ${discountText}`
  const primaryCategory = getCouponLocaleText(store.primaryCategory, language)
  const secondaryCategory = getCouponLocaleText(store.secondaryCategory, language)
  const claimToday = getCouponLocaleText(COUPON_STATIC_TEXT.claimToday, language)
  const participants = formatCouponParticipants(
    store.participantCount,
    COUPON_STATIC_TEXT.participantsSuffix,
    language,
  )

  return (
    <article className="flex h-full overflow-hidden flex-col bg-white rounded-[8px]">
      <Link
        to={`/coupon-detail?id=${encodeURIComponent(store.id)}`}
        className="block overflow-hidden bg-[#F4F4F7]"
        aria-label={title}
      >
        <img
          src={preFixImgUrl(store.listImageFile)}
          alt=""
          className="aspect-[16/9] w-full object-cover transition-transform duration-300 hover:scale-[1.02]"
        />
      </Link>
      <div className="flex flex-1 flex-col px-[26px] pb-[36px] pt-[24px]">
        <div className="flex items-center gap-[12px] text-[20px] leading-none text-[#8E8E96]">
          <span>{primaryCategory}</span>
          <span className="h-[18px] w-px bg-[#D7D7DE]" aria-hidden />
          <span>{secondaryCategory}</span>
        </div>
        <Link
          to={`/coupon-detail?id=${encodeURIComponent(store.id)}`}
          className="mt-[16px] min-h-[64px] text-[28px] leading-[32px] font-bold text-[#050505] no-underline line-clamp-2"
        >
          <CouponCardTitle merchantName={store.merchantName} discountText={discountText} />
        </Link>
        <div className="mt-[16px] inline-flex h-[34px] w-fit items-center rounded-[4px] bg-[#F5F5F5] px-[16px] text-[20px] leading-none text-[#777777]">
          {claimToday}
        </div>
        <div className="mt-[20px] flex min-w-0 items-center text-[16px] leading-none text-[#707070]">
          <StarFilled className="mr-[7px] shrink-0 text-[17px] !text-[#fb5c00]" />
          <span className="mr-[8px] shrink-0 text-[20px] font-bold text-[#fb5c00]">{store.rating}</span>
          <span className="shrink-0">({store.reviewCount})</span>
          <span className="mx-[5px] shrink-0">·</span>
          <span className="min-w-0 truncate">{participants}</span>
        </div>
        <Link
          to={`/coupon-detail?id=${encodeURIComponent(store.id)}`}
          className="mt-[30px] inline-flex h-[56px] w-full items-center justify-center rounded-full border border-primary bg-secondary text-[24px] font-medium text-primary no-underline transition-opacity hover:opacity-90"
        >
          <LangText lngKey="common.action.claimFree" />
        </Link>
      </div>
    </article>
  )
}

const CouponList = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const { currentPage, totalPages, coupons } = useMemo(() => {
    const pageCount = Math.ceil(TEMP_COUPON_STORES.length / TEMP_COUPON_PAGE_SIZE)
    const pageIndex = readPageIndex(searchParams.get('page'), pageCount)
    const startIndex = pageIndex * TEMP_COUPON_PAGE_SIZE

    return {
      currentPage: pageIndex,
      totalPages: pageCount,
      coupons: TEMP_COUPON_STORES.slice(startIndex, startIndex + TEMP_COUPON_PAGE_SIZE),
    }
  }, [searchParams])

  const goToPage = useCallback(
    (pageIndex: number) => {
      if (pageIndex < 0 || pageIndex >= totalPages || pageIndex === currentPage) return

      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams)

          if (pageIndex === 0) {
            nextParams.delete('page')
          } else {
            nextParams.set('page', String(pageIndex + 1))
          }

          return nextParams
        },
        { replace: true },
      )
    },
    [currentPage, setSearchParams, totalPages],
  )

  return (
    <div className="min-w-[1200px] bg-[#FAFAFA]">
      <HeroBanner />

      <section className="w-full pb-[120px] pt-[82px]">
        <div className="mx-auto w-[1200px]">
          <div className="flex flex-col items-center">
            <LangText
              lngKey="coupon.recommendations.title"
              className="text-[36px] font-bold leading-[50px] text-[#000]"
            />
            <span className="mt-[20px] h-[4px] w-[104px] rounded-full bg-primary" />
          </div>

          <div className="mt-[60px] flex items-center justify-between">
            <div className="flex items-center gap-[12px]">
              {COUPON_TABS.map((tab) => {
                const isActive = tab.id === 'shopping'

                return (
                  <button
                    key={tab.id}
                    type="button"
                    disabled={!isActive}
                    className={`h-[40px] min-w-[100px] rounded-[8px] px-[18px] text-[16px] transition-colors disabled:cursor-not-allowed ${
                      isActive
                        ? 'border border-primary bg-secondary text-primary'
                        : 'border border-transparent bg-[#F2F2F2] text-[#979797]'
                    }`}
                  >
                    <LangText lngKey={tab.labelKey} />
                  </button>
                )
              })}
            </div>
          </div>

          <div className="mt-[24px] grid grid-cols-3 gap-[24px]">
            {coupons.map((store) => (
              <CouponCard key={store.id} store={store} />
            ))}
          </div>

          {totalPages > 1 && (
            <nav
              className="mt-[48px] flex items-center justify-center gap-[24px]"
              aria-label={t('coupon.list.pagination.navigationLabel')}
            >
              <button
                type="button"
                disabled={currentPage === 0}
                onClick={() => goToPage(currentPage - 1)}
                aria-label={t('coupon.list.pagination.previous')}
                className="flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-full border border-primary bg-white text-primary transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:border-[#D8D8D8] disabled:text-[#B0B0B0] disabled:hover:bg-white"
              >
                <LeftOutlined />
              </button>

              <LangText
                lngKey="coupon.list.pagination.pageIndicator"
                values={{ current: currentPage + 1, total: totalPages }}
                className="min-w-[80px] text-center text-[16px] leading-[24px] text-[#666]"
              />

              <button
                type="button"
                disabled={currentPage === totalPages - 1}
                onClick={() => goToPage(currentPage + 1)}
                aria-label={t('coupon.list.pagination.next')}
                className="flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-full border border-primary bg-white text-primary transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:border-[#D8D8D8] disabled:text-[#B0B0B0] disabled:hover:bg-white"
              >
                <RightOutlined />
              </button>
            </nav>
          )}
        </div>
      </section>

      <QuickLinks />
    </div>
  )
}

export default CouponList
